import React, { useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { words, upperFirst, startsWith, lowerCase } from 'lodash';
import { Modal, Input, Divider } from 'antd';
import { useHotkeys } from 'react-hotkeys-hook';
import { getRouterData } from '../../common/router';
import { USER_ROLES_MAP } from 'utils/constants';

import styles from './NavigateModal.less';

const { Search } = Input;

const routerData = getRouterData();

const availableRoutes = (() => {
  const adminRoutes = routerData['/admin'];
  const orgRoutes = routerData['/organization'];
  const helpCenterRoutes = routerData['/help-center'];
  const routes = Object.values([
    ...adminRoutes.routes,
    ...orgRoutes.routes,
    ...helpCenterRoutes.routes,
  ]).filter((route) => !route.path.includes(':') || route.showInNavigateModal);

  return routes;
})();

function NavigateModal() {
  const history = useHistory();

  const user = useSelector(({ auth }) => auth.user);
  const isShortcutModalAdmin = user.roles.includes(USER_ROLES_MAP.shortcutModalAdmin);
  const isAllowed = user.isSuperAdmin && isShortcutModalAdmin;

  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedResult, setSelectedResult] = useState(0);
  const searchRef = useRef(null);

  const searchedRoutes = useMemo(() => {
    if (!search) return [];

    const lowerCaseSearch = lowerCase(search);
    const result = [];

    let unprocessedRoutes = [];

    availableRoutes.forEach((route) => {
      const searchedKey = lowerCase(route.key).replace(' ', '');
      const searchedName = lowerCase(route.path).replace(' ', '');
      if (startsWith(searchedName, lowerCaseSearch) || startsWith(searchedKey, lowerCaseSearch)) {
        result.push(route);
      } else {
        unprocessedRoutes.push(route);
      }
    });

    unprocessedRoutes.forEach((route) => {
      const routeName = words(route.key).map(upperFirst).join(' ');
      if (
        routeName.toLowerCase().includes(search.toLowerCase()) ||
        route.path.includes(search.toLowerCase())
      ) {
        result.push(route);
      }
    });

    return result.slice(0, 10);
  }, [search]);

  useHotkeys('ctrl+k, command+k', (e) => {
    e.preventDefault();
    setVisible((prev) => !prev);
    if (searchRef.current) searchRef.current.focus();
  });

  useHotkeys(
    'up',
    (e) => {
      e.preventDefault();
      setSelectedResult((prev) => {
        if (prev === 0) return searchedRoutes.length - 1;
        return prev - 1;
      });
    },
    {
      enableOnTags: ['INPUT'],
    }
  );

  useHotkeys(
    'down',
    (e) => {
      e.preventDefault();
      setSelectedResult((prev) => {
        if (prev === searchedRoutes.length - 1) return 0;
        return prev + 1;
      });
    },
    {
      enableOnTags: ['INPUT'],
    }
  );

  const onCancel = () => {
    setVisible(false);
    setSearch('');
    setSelectedResult(0);
  };

  const goToPage = (route) => {
    history.push(generatePath(route.path));
    onCancel();
  };

  if (!isAllowed) return null;

  return (
    <Modal title="Navigate to page..." footer={null} visible={visible} onCancel={onCancel}>
      <Search
        ref={searchRef}
        value={search}
        placeholder="Go to page..."
        onChange={(e) => {
          setSearch(e.target.value);
          setSelectedResult(0);
        }}
        onSearch={() => {
          if (!searchedRoutes?.[selectedResult]) return;
          goToPage(searchedRoutes[selectedResult]);
        }}
      />
      <Divider />
      {!searchedRoutes.length && search && <div>No pages found.</div>}
      <div className={styles.linkContainer}>
        {searchedRoutes.map((route, i) => (
          <div
            className={classnames(styles.linkBox, i === selectedResult && styles.selected)}
            key={route.path}
            onClick={() => goToPage(route)}
          >
            <h3>{words(route.key).map(upperFirst).join(' ')}</h3>
            <i>{route.path}</i>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export { NavigateModal };
